/**
 * Quiz scoring — pure functions (testable via Node)
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = factory();
  } else {
    root.QuizScoring = factory();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var PROFILE_ORDER = ['pratico', 'confortavel', 'elegante', 'presenteador'];

  function emptyScores() {
    return { pratico: 0, confortavel: 0, elegante: 0, presenteador: 0 };
  }

  /**
   * @param {Record<string, number>} answers - questionId -> choiceIndex (0-based)
   * @param {Array<{id: string, required: boolean, choices: Array<{weights: Record<string, number>}>}>} questions
   */
  function calculateScores(answers, questions) {
    var scores = emptyScores();

    questions.forEach(function (question) {
      var choiceIndex = answers[question.id];
      if (choiceIndex === undefined || choiceIndex === null || choiceIndex < 0) {
        return;
      }
      var choice = question.choices[choiceIndex];
      if (!choice || !choice.weights) {
        return;
      }
      PROFILE_ORDER.forEach(function (profile) {
        scores[profile] += choice.weights[profile] || 0;
      });
    });

    return scores;
  }

  /**
   * @param {Record<string, number>} scores
   * @returns {string}
   */
  function getWinner(scores) {
    var best = PROFILE_ORDER[0];
    var bestScore = scores[best] || 0;

    for (var i = 1; i < PROFILE_ORDER.length; i++) {
      var profile = PROFILE_ORDER[i];
      var value = scores[profile] || 0;
      if (value > bestScore) {
        best = profile;
        bestScore = value;
      }
    }

    return best;
  }

  /**
   * @param {string} profileId
   * @param {Array<{profileId: string}>} results
   */
  function findResult(profileId, results) {
    if (!results || !results.length) {
      return null;
    }
    for (var i = 0; i < results.length; i++) {
      if (results[i].profileId === profileId) {
        return results[i];
      }
    }
    return null;
  }

  /**
   * Progress percent based on current question index (0-based)
   */
  function getProgressPercent(currentIndex, totalQuestions) {
    if (!totalQuestions || totalQuestions <= 0) {
      return 0;
    }
    var clamped = Math.max(0, Math.min(currentIndex + 1, totalQuestions));
    return Math.round((clamped / totalQuestions) * 100);
  }

  return {
    PROFILE_ORDER: PROFILE_ORDER,
    calculateScores: calculateScores,
    getWinner: getWinner,
    findResult: findResult,
    getProgressPercent: getProgressPercent,
    emptyScores: emptyScores,
  };
});

/**
 * Quiz page UI
 */
(function () {
  'use strict';

  var Scoring = typeof QuizScoring !== 'undefined' ? QuizScoring : null;
  if (!Scoring) {
    return;
  }

  var rootEl = null;
  var config = null;
  var state = {
    currentIndex: 0,
    answers: {},
    totalQuestions: 0,
  };

  function parseConfig() {
    var script = document.getElementById('quiz-config-json');
    if (!script) {
      return null;
    }
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function getQuestionEls() {
    return rootEl ? rootEl.querySelectorAll('.quiz__step[data-question-id]') : [];
  }

  function getActiveQuestion() {
    return config.questions[state.currentIndex] || null;
  }

  function hasValidAnswer(question) {
    if (!question) {
      return true;
    }
    if (!question.required) {
      return true;
    }
    var answer = state.answers[question.id];
    return answer !== undefined && answer !== null && answer >= 0;
  }

  function canAdvance() {
    var question = getActiveQuestion();
    return hasValidAnswer(question);
  }

  function updateNav() {
    var prevBtn = rootEl.querySelector('[data-quiz-action="prev"]');
    var nextBtn = rootEl.querySelector('[data-quiz-action="next"]');

    if (prevBtn) {
      prevBtn.hidden = state.currentIndex <= 0;
    }

    if (nextBtn) {
      var isLast = state.currentIndex >= state.totalQuestions - 1;
      nextBtn.textContent = isLast
        ? config.labels.finish || 'Ver resultado'
        : config.labels.next || 'Avançar';

      var valid = canAdvance();
      nextBtn.disabled = !valid;
      nextBtn.setAttribute('aria-disabled', valid ? 'false' : 'true');
    }
  }

  function updateProgress() {
    var bar = rootEl.querySelector('.quiz__progress-fill');
    var label = rootEl.querySelector('.quiz__progress-text');
    var percent = Scoring.getProgressPercent(state.currentIndex, state.totalQuestions);

    if (bar) {
      bar.style.width = percent + '%';
    }
    if (label) {
      label.textContent = percent + '%';
    }

    var progressBar = rootEl.querySelector('.quiz__progress');
    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', String(percent));
    }
  }

  function showStep(index) {
    state.currentIndex = index;

    rootEl.querySelectorAll('.quiz__step').forEach(function (el) {
      el.classList.remove('quiz__step--active');
      el.hidden = true;
    });

    var questionEls = getQuestionEls();
    if (index < questionEls.length) {
      var step = questionEls[index];
      step.classList.add('quiz__step--active');
      step.hidden = false;
      var focusTarget = step.querySelector('input[type="radio"]');
      if (focusTarget) {
        focusTarget.focus();
      }
    }

    updateNav();
    updateProgress();
  }

  function showResult() {
    var scores = Scoring.calculateScores(state.answers, config.questions);
    var winner = Scoring.getWinner(scores);
    var result = Scoring.findResult(winner, config.results);
    var resultStep = rootEl.querySelector('.quiz__step--result');

    rootEl.querySelectorAll('.quiz__step[data-question-id]').forEach(function (el) {
      el.classList.remove('quiz__step--active');
      el.hidden = true;
    });

    if (!resultStep) {
      return;
    }

    var messageEl = resultStep.querySelector('[data-quiz-result-message]');
    var ctaEl = resultStep.querySelector('[data-quiz-result-cta]');
    var imageWrap = resultStep.querySelector('[data-quiz-result-image]');

    if (result) {
      if (messageEl) {
        messageEl.innerHTML = result.messageHtml || '';
      }
      if (ctaEl) {
        ctaEl.href = result.ctaUrl || config.fallbackCtaUrl || '/collections/all';
        ctaEl.textContent = result.ctaLabel || config.labels.cta || 'Explorar coleção';
        ctaEl.hidden = false;
      }
      if (imageWrap && result.imageUrl) {
        imageWrap.innerHTML =
          '<img src="' +
          result.imageUrl +
          '" alt="" width="400" height="300" loading="lazy" class="quiz__result-img">';
        imageWrap.hidden = false;
      } else if (imageWrap) {
        imageWrap.hidden = true;
        imageWrap.innerHTML = '';
      }
    } else {
      if (messageEl) {
        messageEl.innerHTML = config.fallbackMessageHtml || '';
      }
      if (ctaEl) {
        ctaEl.href = config.fallbackCtaUrl || '/collections/all';
        ctaEl.textContent = config.labels.cta || 'Explorar coleção';
        ctaEl.hidden = false;
      }
      if (imageWrap) {
        imageWrap.hidden = true;
        imageWrap.innerHTML = '';
      }
    }

    resultStep.classList.add('quiz__step--active');
    resultStep.hidden = false;

    var prevBtn = rootEl.querySelector('[data-quiz-action="prev"]');
    var nextBtn = rootEl.querySelector('[data-quiz-action="next"]');
    if (prevBtn) {
      prevBtn.hidden = false;
    }
    if (nextBtn) {
      nextBtn.hidden = true;
    }

    var bar = rootEl.querySelector('.quiz__progress-fill');
    if (bar) {
      bar.style.width = '100%';
    }
    var label = rootEl.querySelector('.quiz__progress-text');
    if (label) {
      label.textContent = '100%';
    }

    resultStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function bindChoices() {
    rootEl.querySelectorAll('.quiz__choice-input').forEach(function (input) {
      input.addEventListener('change', function () {
        var questionId = input.getAttribute('data-question-id');
        var choiceIndex = parseInt(input.getAttribute('data-choice-index'), 10);
        state.answers[questionId] = choiceIndex;
        updateNav();
      });
    });
  }

  function bindNav() {
    var prevBtn = rootEl.querySelector('[data-quiz-action="prev"]');
    var nextBtn = rootEl.querySelector('[data-quiz-action="next"]');

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        var resultStep = rootEl.querySelector('.quiz__step--result');
        if (resultStep && !resultStep.hidden) {
          resultStep.classList.remove('quiz__step--active');
          resultStep.hidden = true;
          showStep(state.totalQuestions - 1);
          if (nextBtn) {
            nextBtn.hidden = false;
          }
          return;
        }
        if (state.currentIndex > 0) {
          showStep(state.currentIndex - 1);
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!canAdvance()) {
          return;
        }
        if (state.currentIndex >= state.totalQuestions - 1) {
          showResult();
          return;
        }
        showStep(state.currentIndex + 1);
      });
    }
  }

  function init() {
    rootEl = document.querySelector('.quiz');
    if (!rootEl) {
      return;
    }

    config = parseConfig();
    if (!config || !config.questions || !config.questions.length) {
      return;
    }

    state.totalQuestions = config.questions.length;
    state.currentIndex = 0;
    state.answers = {};

    bindChoices();
    bindNav();
    showStep(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
